import api from './api';
import fileUploadService from './fileUploadService';
import { deduplicateMessages } from '../utils/deduplicateMessages';

// The baseURL from api.js already includes '/api' as: http://localhost:5000/api
// We need to ensure we don't duplicate it

const conversationService = {
  // Get all conversations for a project
  getConversations: async (projectId) => {
    try {
      console.log(`Fetching conversations for project: ${projectId}`);
      const response = await api.get(`/conversations/projects/${projectId}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      throw error.response?.data?.message || 'Failed to fetch conversations';
    }
  },

  // Get a single conversation
  getConversation: async (conversationId) => {
    try {
      console.log(`Fetching conversation: ${conversationId}`);
      const response = await api.get(`/conversations/${conversationId}`);
      const conversation = response.data.data;
      
      // Deduplicate messages before returning
      if (conversation && conversation.messages) {
        conversation.messages = deduplicateMessages(conversation.messages);
      }
      
      return conversation;
    } catch (error) {
      console.error("Error fetching conversation:", error);
      throw error.response?.data?.message || 'Failed to fetch conversation';
    }
  },

  // Create a new conversation
  createConversation: async (projectId, data) => {
    try {
      console.log(`Creating conversation for project: ${projectId}`);
      
      // Handle both string (backward compatibility) and object format
      const requestData = typeof data === 'string' 
        ? { title: data } 
        : data;
        
      const response = await api.post(`/conversations/projects/${projectId}`, requestData);
      return response.data.data;
    } catch (error) {
      console.error("Error creating conversation:", error);
      throw error.response?.data?.message || 'Failed to create conversation';
    }
  },

  // Add a message to a conversation without triggering AI processing
  addMessage: async (conversationId, content, role = 'user', attachments = []) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/messages/add`, {
        content,
        role,
        attachments
      });
      
      return response.data;
    } catch (error) {
      console.error("Error adding message:", error);
      throw error.response?.data?.message || error.message || 'Failed to add message';
    }
  },
  
  // Enhanced file upload with Claude integration
  uploadFiles: async (conversationId, files, message = '') => {
    try {
      if (files.length === 0) {
        throw new Error('No files to upload');
      }
      
      console.log(`Uploading ${files.length} file(s) to conversation: ${conversationId}`);
      
      // Upload files using the new file upload service
      const uploadedFiles = [];
      
      for (const file of files) {
        // Validate file before upload
        const validation = fileUploadService.validateFile(file);
        if (!validation.isValid) {
          throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
        }
        
        console.log(`Uploading file: ${file.name} (${fileUploadService.formatFileSize(file.size)})`);
        
        // Upload file with Claude integration
        const uploadedFile = await fileUploadService.uploadFile(file, {
          conversationId: conversationId,
          description: message || `File uploaded to conversation`,
          purpose: 'content'
        });
        
        uploadedFiles.push(uploadedFile);
      }
      
      // Send message with file attachments to conversation
      if (message.trim()) {
        // Prepare file attachments for the message
        const fileAttachments = uploadedFiles.map(file => ({
          file_id: file.id,
          claude_file_id: file.claude_file_id,
          filename: file.filename,
          content_type: file.content_type,
          purpose: file.purpose || 'content'
        }));
        
        // Send message with file attachments
        const messageResponse = await api.post(`/conversations/${conversationId}/messages`, {
          content: message,
          file_attachments: fileAttachments
        });
        
        console.log(`Message sent with ${fileAttachments.length} file attachments`);
        return messageResponse.data;
      }
      
      // Return upload results if no message to send
      return {
        success: true,
        uploadedFiles,
        message: `Successfully uploaded ${uploadedFiles.length} file(s) with Claude integration`
      };
    } catch (error) {
      console.error("Error in enhanced file upload:", error);
      throw new Error('Failed to upload file(s): ' + (error.message || 'Unknown error'));
    }
  },
  
  /**
   * Stream a message response from the server with real-time streaming
   * Uses a direct fetch call with ReadableStream for server-sent events
   * @param {string} conversationId - The ID of the conversation
   * @param {string} content - The user message content
   * @param {Function} onChunk - Callback for each text chunk
   * @param {Function} onComplete - Callback when streaming completes
   * @param {Function} onError - Callback for errors
   * @returns {Promise} - Promise that resolves when streaming completes
   */
  streamMessage: (conversationId, content, onChunk, onComplete, onError, attachments = []) => {
    console.log(`Initiating message stream for conversation: ${conversationId}`);
    
    // Create an AbortController for cancellation
    const abortController = new AbortController();
    
    // Create the abort function that will be returned immediately
    const abortFunction = () => {
      console.log('Abort function called - canceling stream');
      abortController.abort();
    };
    
    // Start the async streaming process
    const streamPromise = (async () => {
    
    try {
      // Skip duplicate check - backend handles this now
      console.log("Streaming message without duplicate check");
      
      // Ensure we have a valid token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        console.error('No auth token found for stream request');
        throw new Error('Authentication token missing');
      }
      
      // Create URL object
      const url = new URL(`${api.defaults.baseURL}/conversations/${conversationId}/messages/stream`);
      
      // Make the streaming request
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          content,
          role: 'user',
          preventDuplicates: true,
          // attachments: attachments || []
          attachments: []
        }),
        mode: 'cors',
        signal: abortController.signal
      });
      
      // Handle non-success responses
      if (!response.ok) {
        console.error(`Stream request failed with status: ${response.status}`);
        
        // Try to extract error details from response
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || `Server returned ${response.status}: ${response.statusText}`;
        } catch (jsonError) {
          errorMessage = `Server returned ${response.status}: ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }
      
      console.log('Stream request successful, status:', response.status);
      console.log('Response headers:', response.headers);
      
      // Ensure browser supports ReadableStream
      if (!response.body) {
        console.error('ReadableStream not supported in this browser');
        throw new Error('ReadableStream not supported in this browser.');
      }
      
      // Get the reader from the response body stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let receivedText = '';
      let done = false;
      
      // Create abort function that can access reader
      const abortFunction = () => {
        console.log('Abort function called - canceling stream');
        abortController.abort();
        // Also try to cancel the reader
        reader.cancel().catch(() => {
          // Ignore errors when canceling
        });
      };
      
      console.log('Stream connected, awaiting data...');
      
      // Much simpler stream reading approach with fewer callbacks to reduce flicker
      console.log("Starting stream read - simplified approach");
      
      try {
        // Use a simple async/await loop instead of recursion - more stable
        while (true) {
          // Check if aborted
          if (abortController.signal.aborted) {
            console.log('Stream aborted by user');
            break;
          }
          
          // Read the next chunk
          const { done, value } = await reader.read();
          
          // If done, call completion and exit
          if (done) {
            console.log('Stream complete, final length:', receivedText.length);
            // Call onComplete asynchronously to avoid React state update issues
            setTimeout(() => {
              onComplete && onComplete(receivedText);
            }, 0);
            break;
          }
          
          // Decode the chunk
          const rawChunk = decoder.decode(value, { stream: true });
          
          // Parse SSE data format - extract content after "data: " prefix
          // Filter out SSE comments (": ping" keep-alive) and empty lines
          let cleanContent = '';
          const sseLines = rawChunk.split('\n');
          for (const line of sseLines) {
            if (line.startsWith('data: ')) {
              cleanContent += line.substring(6);
            } else if (line.startsWith(':')) {
              // SSE comment (keep-alive ping) — skip
              continue;
            } else if (line.trim() === '') {
              continue;
            } else if (!rawChunk.includes('data: ') && !line.startsWith(':')) {
              // Raw content without SSE framing
              cleanContent += line;
            }
          }

          // Skip if nothing left after filtering pings
          if (!cleanContent || !cleanContent.trim()) {
            continue;
          }
          
          // Check for status events (post-streaming processing indicators)
          const statusMatch = cleanContent.match(/<status>(.*?)<\/status>/);
          if (statusMatch) {
            const statusText = statusMatch[1];
            console.log(`📊 Processing status: ${statusText}`);
            // Send status as a special chunk — the UI component can detect and show as indicator
            onChunk && onChunk(`\n\n⏳ ${statusText}\n\n`, receivedText + `\n\n⏳ ${statusText}\n\n`);
            await new Promise(resolve => setTimeout(resolve, 10));
            continue; // Don't add status tags to accumulated text
          }

          // Add to the accumulated text
          receivedText += cleanContent;

          // Log meaningful chunks
          if (cleanContent.length > 10 || cleanContent.includes('\n')) {
            console.log(`Received significant chunk (${cleanContent.length} chars), total: ${receivedText.length}`);
          }

          // Call chunk callback, but only for substantial updates
          if (cleanContent.length >= 2) {
            onChunk && onChunk(cleanContent, receivedText);

            // Small delay to allow UI to update - but only for significant chunks
            if (cleanContent.length > 10 || cleanContent.includes('\n')) {
              await new Promise(resolve => setTimeout(resolve, 5));
            }
          }
        }
      } catch (error) {
        console.error('Error reading stream:', error);
        // Only call onError if not aborted
        if (error.name !== 'AbortError' && !abortController.signal.aborted) {
          onError && onError(error);
        }
      } finally {
        // Always try to cancel the reader on exit
        try {
          await reader.cancel();
        } catch (e) {
          // Ignore errors when canceling
        }
      }
      
      // Return the received text
      return receivedText;
    } catch (error) {
      console.error("Error in streaming:", error);
      
      // Check if it was aborted
      if (error.name === 'AbortError') {
        console.log('Stream was cancelled by user');
        return '';
      }
      
      onError && onError(error);
      
      // No need for duplicate message handling - backend handles it
      
      throw error.response?.data?.message || error.message || 'Failed to stream message';
    }
    })(); // End of async IIFE
    
    // Return immediately with abort function and promise
    return {
      abort: abortFunction,
      promise: streamPromise
    };
  },

  /**
   * Stream a message with specialized section context
   * Uses real-time streaming with section-specific prompting and reference content
   * @param {string} conversationId - The ID of the conversation
   * @param {string} content - The user message content
   * @param {Function} onChunk - Callback for each text chunk
   * @param {Function} onComplete - Callback when streaming completes
   * @param {Function} onError - Callback for errors
   * @param {string} sectionType - The type of section (field-validations, tech-stack, etc.)
   * @param {Object} relatedSections - Object with related section content
   * @returns {Promise} - Promise that resolves when streaming completes
   */
  streamMessageWithContext: (conversationId, content, onChunk, onComplete, onError, sectionType, relatedSections = {}) => {
    console.log(`Streaming with context for ${sectionType}, conversation: ${conversationId}`);
    
    // Create an AbortController for cancellation
    const abortController = new AbortController();
    
    // Create the abort function that will be returned immediately
    const abortFunction = () => {
      console.log('Abort function called - canceling stream with context');
      abortController.abort();
    };
    
    // Start the async streaming process
    const streamPromise = (async () => {
    
    try {
      // Skip duplicate check - backend handles this now
      console.log("Streaming message with context without duplicate check");
      
      // Create the section-specialized URL
      let streamUrl;
      
      // Use specialized endpoint if section type is provided
      if (sectionType) {
        const normalizedType = sectionType.toLowerCase().replace(/\s+/g, '-');
        streamUrl = `${api.defaults.baseURL}/conversations/${conversationId}/messages/stream/${normalizedType}`;
        console.log(`Using specialized endpoint for ${normalizedType}`);
      } else {
        streamUrl = `${api.defaults.baseURL}/conversations/${conversationId}/messages/stream`;
      }
      
      // Ensure auth token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Authentication token missing');
      }
      
      // Prepare reference content if available
      const referencesContent = {};
      
      if (relatedSections && Object.keys(relatedSections).length > 0) {
        Object.entries(relatedSections).forEach(([sectionName, content]) => {
          if (content && content.trim()) {
            referencesContent[sectionName.toLowerCase().replace(/\s+/g, '_')] = content;
          }
        });
        console.log(`Including ${Object.keys(referencesContent).length} reference sections`);
      }
      
      // Make the streaming request
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          content,
          role: 'user',
          preventDuplicates: true,
          sectionType: sectionType?.toLowerCase().replace(/\s+/g, '_'),
          references: referencesContent,
          attachments: []
        }),
        mode: 'cors',
        signal: abortController.signal
      });
      
      // Handle non-success responses
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || `Server error: ${response.status}`;
        } catch (jsonError) {
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      // Ensure ReadableStream support
      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser.');
      }
      
      // Read the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let receivedText = '';
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            setTimeout(() => onComplete && onComplete(receivedText), 0);
            break;
          }
          
          const rawChunk = decoder.decode(value, { stream: true });
          
          // Parse SSE data format - extract content after "data: " prefix
          // Filter out SSE comments (": ping" keep-alive) and empty lines
          let cleanContent = '';
          const sseLines = rawChunk.split('\n');
          for (const line of sseLines) {
            if (line.startsWith('data: ')) {
              cleanContent += line.substring(6);
            } else if (line.startsWith(':')) {
              // SSE comment (keep-alive ping) — skip
              continue;
            } else if (line.trim() === '') {
              continue;
            } else if (!rawChunk.includes('data: ') && !line.startsWith(':')) {
              // Raw content without SSE framing
              cleanContent += line;
            }
          }

          // Skip if nothing left after filtering pings
          if (!cleanContent || !cleanContent.trim()) {
            continue;
          }
          
          receivedText += cleanContent;
          
          if (cleanContent.length >= 2) {
            onChunk && onChunk(cleanContent, receivedText);
            
            if (cleanContent.length > 10 || cleanContent.includes('\n')) {
              await new Promise(resolve => setTimeout(resolve, 5));
            }
          }
        }
      } catch (streamError) {
        console.error('Error reading stream:', streamError);
        onError && onError(streamError);
      }
      
      // Return the received text
      return receivedText;
    } catch (error) {
      console.error("Error in streaming with context:", error);
      
      // Check if it was aborted
      if (error.name === 'AbortError') {
        console.log('Stream was cancelled by user');
        return '';
      }
      
      onError && onError(error);
      
      // No need for duplicate message handling - backend handles it
      
      throw error.response?.data?.message || error.message || 'Failed to stream message';
    }
    })(); // End of async IIFE
    
    // Return immediately with abort function and promise
    return {
      abort: abortFunction,
      promise: streamPromise
    };
  },

  // Add an artifact to a conversation
  addArtifact: async (conversationId, type, title, content, metadata = {}) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/artifacts`, {
        type,
        title,
        content,
        metadata
      });
      return response.data.data;
    } catch (error) {
      console.error("Error adding artifact:", error);
      throw error.response?.data?.message || 'Failed to add artifact';
    }
  },
  
  // Save conversation (with enhanced error handling and direct fetch as fallback)
  saveConversation: async (conversationId, conversationData = {}) => {
    try {
      console.log(`Saving conversation: ${conversationId}`);
      
      // Add debugging info
      console.log("API BaseURL:", api.defaults.baseURL);
      console.log("Save payload:", JSON.stringify(conversationData));
      
      // Get auth token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Authentication token missing');
      }
      
      // Try three approaches in sequence:
      // 1. Standard axios api.put
      // 2. Direct fetch to API using axios baseURL
      // 3. Direct fetch to hardcoded URL as final fallback
      
      let response;
      let success = false;
      let lastError = null;
      
      // Attempt 1: Standard axios approach
      try {
        console.log("Attempt 1: Using standard axios PUT");
        response = await api.put(`/conversations/${conversationId}`, conversationData);
        success = true;
      } catch (firstError) {
        console.warn("First save attempt failed:", firstError.message);
        lastError = firstError;
        
        // Attempt 2: Try with direct fetch using the same URL from axios
        try {
          console.log("Attempt 2: Using direct fetch with axios baseURL");
          const fetchUrl = `${api.defaults.baseURL}/conversations/${conversationId}`;
          console.log("Fetch URL:", fetchUrl);
          
          const fetchResponse = await fetch(fetchUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(conversationData)
          });
          
          if (!fetchResponse.ok) {
            throw new Error(`HTTP error! Status: ${fetchResponse.status}`);
          }
          
          response = { data: await fetchResponse.json() };
          success = true;
        } catch (secondError) {
          console.warn("Second save attempt failed:", secondError.message);
          lastError = secondError;
          
          // Attempt 3: Final fallback with hardcoded URL
          try {
            console.log("Attempt 3: Using hardcoded URL fallback");
            const hardcodedUrl = `http://localhost:8003/api/conversations/${conversationId}`;
            console.log("Hardcoded URL:", hardcodedUrl);
            
            const finalResponse = await fetch(hardcodedUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify(conversationData)
            });
            
            if (!finalResponse.ok) {
              throw new Error(`HTTP error! Status: ${finalResponse.status}`);
            }
            
            response = { data: await finalResponse.json() };
            success = true;
          } catch (thirdError) {
            console.error("All save attempts failed:", thirdError);
            lastError = thirdError;
          }
        }
      }
      
      if (!success) {
        throw lastError || new Error("Failed to save conversation after all attempts");
      }
      
      // Log success and return data
      console.log("Save successful, response:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("Error saving conversation:", error);
      
      // More detailed error logging
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      }
      
      throw error.response?.data?.message || error.message || 'Failed to save conversation';
    }
  },

  // Get or create conversation for a specific task (optimized to avoid multiple API calls)
  getOrCreateConversation: async (projectId, taskId, title, docMode, orgDocId) => {
    try {
      console.log(`Getting or creating conversation for project: ${projectId}, task: ${taskId}, docMode: ${docMode}`);
      const params = new URLSearchParams();
      if (taskId) params.append('task_id', taskId);
      if (docMode) params.append('doc_mode', docMode);
      if (orgDocId) params.append('org_doc_id', orgDocId);
      const query = params.toString();
      const url = `/conversations/projects/${projectId}/get-or-create${query ? `?${query}` : ''}`;
      const response = await api.get(url);
      const conversation = response.data.data;
      
      // Deduplicate messages before returning
      if (conversation && conversation.messages) {
        conversation.messages = deduplicateMessages(conversation.messages);
      }
      
      return conversation;
    } catch (error) {
      console.error("Error getting or creating conversation:", error);
      throw error.response?.data?.message || 'Failed to get or create conversation';
    }
  },

  // Get cache status for a conversation
  getCacheStatus: async (conversationId) => {
    try {
      console.log(`Getting cache status for conversation: ${conversationId}`);
      const response = await api.get(`/conversations/${conversationId}/cache-status`);
      return response.data.data;
    } catch (error) {
      console.error("Error getting cache status:", error);
      // Return default cache status if API call fails
      return {
        conversation_id: conversationId,
        is_cached: false,
        cache_created_at: null,
        cache_expires_at: null,
        messages_cached: 0,
        total_cache_creation_tokens: 0,
        total_cache_read_tokens: 0,
        time_until_expiry: null
      };
    }
  },

  // Generate document from conversation
  generateDocument: async (conversationId, taskId) => {
    try {
      console.log(`Generating document for conversation: ${conversationId} with task: ${taskId}`);
      
      // Map taskId to document type for Python backend
      const documentTypeMap = {
        'project-info': 'PRD',
        'chat-prd': 'PRD',
        'features': 'FEATURES',
        'user-stories': 'USER_STORIES',
        'user-roles': 'USER_ROLES',
        'field-validations': 'FIELD_VALIDATION',
        'tech-stack': 'TECH_STACK',
        'dev-guidelines': 'DEV_GUIDELINES',
        'design-guidelines': 'DESIGN_GUIDELINES',
        'security-guidelines': 'SECURITY_GUIDELINES',
        'api-guidelines': 'API_GUIDELINES',
        'architecture': 'ARCHITECTURE',
        'process-flows': 'FLOWS',
        'wireframes': 'WIREFRAMES',
        'designs': 'DESIGNS',
        'unit-testing': 'UNIT_TESTING',
        'user-permissions': 'USER_PERMISSIONS',
        'functional-testing': 'FUNCTIONAL_TESTING'
      };
      
      const documentType = documentTypeMap[taskId] || 'PRD';
      
      // Pass the required fields for Python backend
      const response = await api.post(`/conversations/${conversationId}/generate-document`, {
        includeFullConversation: true,
        taskId: taskId,
        document_type: documentType
      });
      return response.data.data;
    } catch (error) {
      console.error("Error generating document:", error);
      throw error.response?.data?.message || 'Failed to generate document';
    }
  },

  // Get persistent files for a conversation
  getPersistentFiles: async (conversationId) => {
    try {
      const response = await api.get(`/conversations/${conversationId}/persistent-files`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error getting persistent files:", error);
      throw error.response?.data?.message || 'Failed to get persistent files';
    }
  },

  // Add a file to persistent files list
  addPersistentFile: async (conversationId, fileData) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/persistent-files`, fileData);
      return response.data;
    } catch (error) {
      console.error("Error adding persistent file:", error);
      throw error.response?.data?.message || 'Failed to add persistent file';
    }
  },

  // Remove a file from persistent files list
  removePersistentFile: async (conversationId, fileId) => {
    try {
      const response = await api.delete(`/conversations/${conversationId}/persistent-files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error("Error removing persistent file:", error);
      throw error.response?.data?.message || 'Failed to remove persistent file';
    }
  },

  // Send a message and get a response (non-streaming)
  sendMessage: async (conversationId, message) => {
    try {
      console.log(`Sending message to conversation: ${conversationId}`);
      
      // Ensure we have auth token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Authentication token missing');
      }
      
      // Send the message and wait for response
      const response = await api.post(`/conversations/${conversationId}/messages`, {
        content: message.content,
        role: 'user',
        attachments: message.attachments || []
      });
      
      // Return the assistant's response
      if (response.data && response.data.data) {
        // Look for the assistant's response in the returned data
        const messages = response.data.data.messages || [];
        const assistantResponse = messages[messages.length - 1];
        return assistantResponse || { content: '' };
      }
      
      return { content: '' };
    } catch (error) {
      console.error("Error sending message:", error);
      throw error.response?.data?.message || 'Failed to send message';
    }
  },

  // Request explanation for a specific section
  requestExplanation: async (explanationData) => {
    try {
      console.log('Requesting explanation for section:', explanationData.sectionTitle);
      
      // Construct explanation prompt
      const explanationPrompt = `
Please provide a brief, clear explanation of the following ${explanationData.sectionType} section.

Section Title: ${explanationData.sectionTitle}

Section Content:
${explanationData.sectionContent}

Instructions:
1. Explain what this section means in simple terms
2. Why it's important for the project
3. Keep it concise (5 lines maximum)

Return ONLY the explanation text, no titles or headers.
`;

      // Get or create a conversation for explanations
      const explanationConversation = await conversationService.getOrCreateConversation(
        explanationData.projectId,
        'explanations',
        'Feature Explanations'
      );
      
      console.log('Created/got explanation conversation:', explanationConversation._id);
      
      // Use streaming to get explanation
      let explanation = '';
      
      await new Promise((resolve, reject) => {
        const streamResult = conversationService.streamMessage(
          explanationConversation._id,
          explanationPrompt,
          (chunk) => {
            // Accumulate chunks
            explanation += chunk;
          },
          (fullContent) => {
            // Streaming complete
            explanation = fullContent;
            resolve();
          },
          (error) => {
            // Error occurred
            reject(error);
          }
        );
      });
      
      if (explanation) {
        return {
          explanation: explanation.trim()
        };
      }
      
      throw new Error('No explanation received');
    } catch (error) {
      console.error("Error requesting explanation:", error);
      throw error.response?.data?.message || error.message || 'Failed to request explanation';
    }
  },

  // Get the latest parsed response (sections from ParsedResponse — source of truth)
  getLatestParsedResponse: async (conversationId) => {
    try {
      const response = await api.get(
        `/conversations/${conversationId}/parsed-responses/latest`
      );
      return response.data?.data || null;
    } catch (error) {
      console.error("Error fetching latest parsed response:", error);
      return null;
    }
  },

  // Update parsed sections directly (JSON-first save)
  // Syncs markdown back to conversation message on the backend
  updateParsedSections: async (conversationId, messageTimestamp, sections) => {
    try {
      const response = await api.put(
        `/conversations/${conversationId}/parsed-responses/${encodeURIComponent(messageTimestamp)}`,
        { sections }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating parsed sections:", error);
      throw error.response?.data?.message || error.message || 'Failed to update parsed sections';
    }
  },

  // ── Artifact Patch (human-in-the-loop) ──

  /**
   * Phase 1: Propose surgical patches without applying.
   * Haiku analyzes the message against existing sections and returns
   * proposed old_str/new_str changes for user confirmation.
   *
   * @param {string} conversationId
   * @param {string} message - User's change request
   * @returns {Promise<{success, proposals, summary, is_question}>}
   */
  proposePatch: async (conversationId, message) => {
    try {
      const response = await api.post(
        `/conversations/${conversationId}/propose-patch`,
        { message }
      );
      return response.data;
    } catch (error) {
      console.error("Error proposing patch:", error);
      throw error.response?.data?.detail || error.message || 'Failed to propose patch';
    }
  },

  /**
   * Phase 2: Apply user-confirmed patches to the artifact.
   * Called after user reviews and clicks "Apply".
   *
   * @param {string} conversationId
   * @param {Array} patches - Array of {section_id, old_str, new_str}
   * @param {string} summary - Human-readable summary
   * @returns {Promise<{success, changed_section_ids, markdown, summary}>}
   */
  applyPatch: async (conversationId, patches, summary) => {
    try {
      const response = await api.post(
        `/conversations/${conversationId}/apply-patch`,
        { patches, summary }
      );
      return response.data;
    } catch (error) {
      console.error("Error applying patch:", error);
      throw error.response?.data?.detail || error.message || 'Failed to apply patch';
    }
  },
};

export default conversationService;